
import { MIN_ODOMETER_GAP } from '@util/odometerService';
import { getTripDetailError, TripDetailsError } from '@util/tripDetailsError';
import { tripToTripMutation } from '@util/tripMapper';
import { Component, Prop, Watch } from 'vue-property-decorator';
import Vue from 'vue';
import AppPage from '@components/new-styleguide/AppPage.vue';
import AppHeader from "@components/new-styleguide/AppHeader.vue";
import AppTripHeader from '@components/trip-detail/AppTripHeader.vue';
import { namespace } from 'vuex-class';
import { GetTrip, Pilot, Tag, Trip, TripMutation } from '@clients/myTrips';
import { LoadState } from '@util/types';
import storageService from '@util/storageService';
import AppContent from '@components/new-styleguide/AppContent.vue';
import { formatDateTimeString } from '@util/dates-and-times';
import TripNotFoundSection from '@components/trip-detail/trip-detail/TripNotFoundSection.vue';
import TripLoadErrorSection from '@components/trip-detail/trip-detail/TripLoadErrorSection.vue';
import TripDetailMap from '@components/trip-detail/trip-detail/TripDetailMap.vue';
import TripStatisticBar from '@components/new-styleguide/TripStatisticBar.vue';
import TripDetailTabs, { AddType } from '@components/trip-detail/trip-detail/TripDetailTabs.vue';
import TripDetailFooter from '@components/trip-detail/trip-detail/TripDetailFooter.vue';
import { RouterQuery } from '@router/Routes';
import { deepEquals } from '@util/copy';
import goBackWithFallback from '@util/goBackWithFallback';
import { RouterFrom } from '@/router/Routes';
import { Location } from 'vue-router';
import { isNumber } from 'util';

const TriplogModule = namespace('Triplog');
const MembershipModule = namespace('Membership');
const SettingsModule = namespace('Settings');
const RouterModule = namespace('Router');

@Component({
  name: 'TripDetail',
  components: {
    AppHeader,
    TripLoadErrorSection,
    TripNotFoundSection,
    TripDetailFooter,
    TripDetailTabs,
    TripStatisticBar,
    TripDetailMap,
    AppContent,
    AppTripHeader,
    AppPage
  }
})
export default class TripDetail extends Vue {
  $refs!: {
    tripDetailTabs: TripDetailTabs
  }

  @Prop({ default: false })
  private addMode: boolean;

  @Prop({ default: false })
  private editMode: boolean;

  @Prop() //editMode
  private tripId: number;

  @Prop()
  private pilotId: number;

  @Prop()
  private tagId: number;

  @Prop() //addMode
  private defaultMutation: TripMutation;

  @Prop() //addMode (optional)
  private gapPrevTrip: Trip;

  @Prop() //addMode (optional)
  private gapNextTrip: Trip;

  @Prop() //addMode (optional)
  private firstTrip: Trip;

  @Prop() //addMode (optional)
  private lastTrip: Trip;

  @Prop({ default: false }) //addMode
  private oldestTripDisabled: boolean;

  @Prop ({ default: false }) //addMode
  private newestTripDisabled: boolean;

  public exitText = 'Ihre Fahrt enthält nicht gespeicherte Änderungen. Diese gehen verloren, wenn Sie die Seite jetzt verlassen. Sind Sie sicher, dass Sie das tun wollen?';

  public deleteText = 'Sind Sie sicher, dass Sie die Fahrt löschen möchten?';

  private mutation: TripMutation = null;

  private loadingNewTrip: boolean = false;

  private BOTTOM_THRESHOLD = 5;

  private singleTrip: GetTrip = null

  private addType: AddType = 'last';

  private addAnotherTrip: boolean = false;

  private showErrorMessage: boolean = true;

  private showNotFound: boolean = false;

  private showLoadError: boolean = false;

  @RouterModule.State('query')
  private query: RouterQuery;

  @TriplogModule.Getter('getTrips') trips;

  @TriplogModule.Getter('getTripsStatus') fetchTripsStatus;

  @TriplogModule.Getter('getTripErrors') tripErrors;

  @TriplogModule.Getter('getTripErrorsStatus') tripErrorsStatus;

  @TriplogModule.Getter('getTripsAreMoreAvailable') areMoreTripsAvailable;

  @TriplogModule.Getter('getSingleTrip') getSingleTrip;

  @TriplogModule.Action('fetchSingleTrip') fetchSingleTrip;

  @TriplogModule.Action('fetchTripErrors') fetchTripErrors;

  @TriplogModule.Mutation('RESET_TRIP_ERRORS') resetTripErrors;

  @TriplogModule.Action('fetchTrips') fetchTrips;

  @TriplogModule.Getter('getDeleteTripStatus') deleteTripStatus;

  @TriplogModule.Action('deleteTrip') deleteTrip;

  @TriplogModule.Getter('getSingleTripStatus') singleTripStatus;

  @TriplogModule.Getter('getVehiclesStatus') vehiclesStatus;

  @SettingsModule.Getter('getTagsStatus') tagsStatus;

  @SettingsModule.Getter('getPilotsStatus') pilotsStatus;

  @SettingsModule.Getter('getZonesStatus') zonesStatus;

  @SettingsModule.Getter('getRulesStatus') rulesStatus;

  @TriplogModule.Getter('getCreateTripStatus') createTripStatus;

  @TriplogModule.Getter('getUpdateTripStatus') updateTripStatus;

  @TriplogModule.Getter('getCreateTrip') createTripResponse;

  @TriplogModule.Getter('getUpdateTrip') updateTripResponse;

  @TriplogModule.Action('createTrip') createTrip;

  @TriplogModule.Mutation('RESET_MUTATE_TRIP') resetMutationRequests;

  @MembershipModule.Getter('hasValidMembership') hasValidMembership;

  @SettingsModule.Action('fetchTags') fetchTags;

  @SettingsModule.Action('fetchPilots') fetchPilots;

  @SettingsModule.Action('fetchZones') fetchZones;

  @SettingsModule.Action('fetchRulesV2') fetchRules;

  @TriplogModule.Action('updateTrip') updateTrip;

  @SettingsModule.Getter('getTags')
  private tags: Tag[];

  @SettingsModule.Getter('getPilots')
  private pilots: Pilot[];

  @RouterModule.State('from')
  private from: RouterFrom;

  async created() {
    window.addEventListener('beforeunload', this.beforeWindowUnload)

    this.resetMutationRequests();

    this.fetchTags();
    this.fetchPilots();
    this.fetchZones();
    this.fetchRules();
  }

  mounted() {
    if (this.newestTripDisabled) {
      this.addType = 'first';
    }
  }

  destroyed() {
    window.removeEventListener('beforeunload', this.beforeWindowUnload)
  }

  private beforeWindowUnload(e) {
    if (this.unsavedChanges) {
      e.preventDefault();
      e.returnValue = this.exitText;
    }
  }

  @Watch('defaultMutation', { immediate: true })
  private handleDefaultMutationChange() {
    if (this.addMode) {
      this.mutation = this.defaultMutation;
    }
    if (this.isGapFill) {
      this.addType = 'gap';
    }

    //update direction data for updated trip mutation
    this.$nextTick(() => {
      if (this.$refs.tripDetailTabs) {
        this.$refs.tripDetailTabs.$refs.tripDetailData.updateNewTripMutation()
      }
    })
  }

  @Watch('tripId', { immediate: true })
  async handleTripIdChange() {
    this.showNotFound = false;
    this.showLoadError = false;

    if (!this.addMode) {
      if (isNaN(this.tripId)) {
        this.$router.replace({ name: 'myTrips' });
      }

      if (this.isTripNearBottomOfList && this.areMoreTripsAvailable && this.fetchTripsStatus !== LoadState.Loading) {
        this.fetchTrips({ vehicleId: storageService.getVehicleId() });
      }

      try {
        this.singleTrip = await this.fetchSingleTrip({ tripId: this.tripId });
      } catch (e) {
        if (e.response.status === 404 || e.response.status === 403) {
          this.showNotFound = true;
        } else {
          this.showLoadError = true;
        }
        return;
      }

      this.setMutationAndChangePilotTag();

      if (this.tripErrorsStatus !== LoadState.Success && this.singleTrip.vehicleId) {
        const from = new Date(this.singleTrip.startInfo.timestamp);
        const until = new Date(this.singleTrip.startInfo.timestamp);
        from.setDate(from.getDate() - 1)
        until.setDate(until.getDate() + 1)
        await this.fetchTripErrors({ vehicleId: this.singleTrip.vehicleId, from, until });
        this.resetTripErrors();
      }
      this.loadingNewTrip = false;
    }
  }

  @Watch('getSingleTrip')
  private updateSingleTrip() {
    if (!this.addMode && (!this.singleTrip || this.getSingleTrip.id === this.singleTrip.id)) {
      this.singleTrip = this.getSingleTrip;
      this.setMutationAndChangePilotTag();
    }
  }

  @Watch('createTripStatus')
  async onCreateErrorChanged() {
    if (this.createTripStatus === LoadState.Error) {
      this.scrollToError();
    }
  }

  @Watch('updateTripStatus')
  async onUpdateErrorChanged() {
    if (this.updateTripStatus === LoadState.Error) {
      this.scrollToError();
    }
  }

  @Watch('newestTripDisabled')
  private onNewestTripDisabled() {
    if (this.newestTripDisabled) {
      this.addType = 'first';
    }
  }

  get events() {
    if (this.singleTrip) {
      return this.singleTrip.events;
    }
    return null;
  }

  get addAnotherDisabled() {
    return this.gapFullyFilled || (this.addMode && this.addType === 'first' &&
      this.tripMutation && this.tripMutation.startInfo && this.tripMutation.startInfo.odometer <= 0.1);
  }

  get requestErrorMessage() {
    if (this.createTripStatus !== LoadState.Error && this.updateTripStatus !== LoadState.Error) {
      return null;
    }

    const response = this.createTripStatus === LoadState.Error ? this.createTripResponse : this.updateTripResponse;
    return this.responseToErrorMessage(response.response.data);
  }

  get errorMessage() {
    if (this.requestErrorMessage && this.showErrorMessage) {
      return this.requestErrorMessage;
    }
    return (this.editMode || this.addMode) && this.saveErrorExists && this.showErrorMessage ? getTripDetailError(this.tripMutation)[0] : null;
  }

  private responseToErrorMessage(responseObject): string {
    if (responseObject.status === 400 && Array.isArray(responseObject.errors) && responseObject.errors.length > 0) {
      const error = responseObject.errors[0];
      if (error.key && error.value) {
        const overlap = error.value.indexOf('overlap') !== -1;

        if (overlap) {
          const value = (error.key.indexOf('odometer') !== -1) ? 'Der Km-Stand' : 'Die Zeit';
          return `Fehler beim Speichern der Fahrt. ${value} überschneidet sich mit einer anderen Fahrt.`;
        }
        const value = (error.key.indexOf('odometer') !== -1) ? 'Km-Stand' : 'Zeit';
        return `Fehler beim Speichern der Fahrt. Es gibt eine ${value}-Lücke zu einer anderen Fahrt.`;
      }
    }
    return 'Fehler beim Speichern der Fahrt. Bitte versuchen sie es später erneut.';
  }

  private scrollToError() {
    document.getElementById('errorMessage').scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }

  private setMutationAndChangePilotTag() {
    this.mutation = tripToTripMutation(this.singleTrip);
    if (this.pilotId && (!this.mutation.pilotId || this.pilotId !== this.mutation.pilotId)) {
      this.mutation.pilotId = this.pilotId;
    }
    if (this.tagId && (!this.mutation.tagId || this.tagId !== this.mutation.tagId)) {
      this.mutation.tagId = this.tagId;
    }
    if (this.pilotId || this.tagId) {
      this.handleSaveButton();
    }
  }

  async deleteTripHandling() {
    if (this.hasValidMembership) {
      await this.deleteTrip({ trip: this.singleTrip });

      if (this.deleteTripStatus === LoadState.Success) {
        goBackWithFallback(this.$router, !!this.from.name, this.backDeleteLocation);
      }
    }
  }

  get isGapFill() {
    return !!this.gapNextTrip && !!this.gapPrevTrip;
  }

  get gapFullyFilled() {
    return !!this.gapNextTrip && !!this.gapPrevTrip && this.gapPrevTrip.endInfo &&
      this.mutation.startInfo.odometer - this.gapPrevTrip.endInfo.odometer < MIN_ODOMETER_GAP &&
      this.gapNextTrip.startInfo.odometer - this.mutation.endInfo.odometer < MIN_ODOMETER_GAP;
  }

  get unsavedChanges() {
    return (this.editMode && !deepEquals(tripToTripMutation(this.singleTrip), this.tripMutation)) || this.addMode;
  }

  get tripMutation() {
    if (this.editMode || this.addMode) {
      return this.mutation;
    }
  }

  get fullscreenMap() {
    return this.query.fullscreenMap === 'true';
  }

  get backLocation() {
    if (this.fullscreenMap) {
      return { query: { ...this.query, fullscreenMap: undefined } };
    } else {
      return { name: 'myTrips' };
    }
  }

  get backDeleteLocation(): Location {
    return { name: 'myTrips', query: { vehicle: this.mutation.vehicleId ? this.mutation.vehicleId.toString() : undefined } };
  }

  get isLoading() {
    return this.vehiclesStatus === LoadState.Loading
      || this.pilotsStatus === LoadState.Loading
      || this.tagsStatus === LoadState.Loading
      || this.zonesStatus === LoadState.Loading
      || this.rulesStatus === LoadState.Loading
      || (!this.singleTrip && !this.addMode)
      || (!this.tripMutation && this.addMode);
  }

  get hasLoadError() {
    return this.vehiclesStatus === LoadState.Error
      || this.pilotsStatus === LoadState.Error
      || this.zonesStatus === LoadState.Error
      || this.rulesStatus === LoadState.Error
      || this.tagsStatus === LoadState.Error;
  }

  get hasTripLoadError() {
    return this.showLoadError || this.showNotFound;
  }

  get deleteDisabled() {
    return this.deleteTripStatus === LoadState.Loading;
  }

  get distance() {
    if ((this.editMode || this.addMode) && this.tripMutation) {
      if (!this.tripMutation.endInfo || !this.tripMutation.startInfo) {
        return 0;
      }
      return Math.max(0, this.tripMutation.endInfo.odometer - this.tripMutation.startInfo.odometer) / 1000;
    } else if (this.singleTrip) {
      if (!this.singleTrip.endInfo || !this.singleTrip.startInfo) {
        return 0;
      }
      return Math.max(0, this.singleTrip.endInfo.odometer - this.singleTrip.startInfo.odometer) / 1000;
    }
    return 0;
  }

  get duration() {
    if ((this.editMode || this.addMode) && this.tripMutation) {
      if (this.tripMutation.endInfo && this.tripMutation.endInfo.timestamp && this.tripMutation.startInfo.timestamp) {
        return (new Date(this.tripMutation.endInfo.timestamp).getTime() - new Date(this.tripMutation.startInfo.timestamp).getTime()) / 60000;
      }
    } else if (this.singleTrip) {
      if (this.singleTrip.endInfo && this.singleTrip.endInfo.timestamp && this.singleTrip.startInfo.timestamp) {
        return (new Date(this.singleTrip.endInfo.timestamp).getTime() - new Date(this.singleTrip.startInfo.timestamp).getTime()) / 60000;
      }
    }
    return 0;
  }

  get speed() {
    if (this.duration === 0) {
      return 0;
    }
    return this.distance / (this.duration / 60);
  }

  get title() {
    if (this.showNotFound) {
      return 'Fahrt nicht gefunden';
    }

    if (this.showLoadError) {
      return 'Fahrtdaten konnten nicht geladen werden';
    }

    if (this.singleTrip && this.singleTrip.startInfo) {
      return 'Fahrt am ' + formatDateTimeString(this.singleTrip.startInfo.timestamp.toString());
    }

    if (this.addMode) {
      return 'Neue Fahrt erstellen';
    }

    return '';
  }

  get isTripNearBottomOfList(): boolean {
    return this.tripIndex !== -1 && this.tripIndex >= this.trips.length - this.BOTTOM_THRESHOLD;
  }

  get tripIndex() {
    if (!this.trips) {
      return -1
    }

    return this.trips.findIndex(trip => trip.id === this.tripId)
  }

  get previousTrip() {
    if (this.addMode) {
      if (this.isGapFill) {
        return this.gapPrevTrip;
      } else if (!this.isGapFill && this.addType === 'last') {
        return this.lastTrip;
      }
      return null;
    } else {
      if (!this.singleTrip || !this.singleTrip.previous || this.errorExists(this.singleTrip.id, this.singleTrip.previous.id)) {
        return null
      }

      return this.singleTrip.previous;
    }
  }

  get nextTrip() {
    if (this.addMode) {
      if (this.isGapFill) {
        return this.gapNextTrip;
      } else if (!this.isGapFill && this.addType === 'first') {
        return this.firstTrip;
      }
      return null;
    } else {
      if (!this.singleTrip || !this.singleTrip.next || this.errorExists(this.singleTrip.id, this.singleTrip.next.id)) {
        return null
      }

      return this.singleTrip.next;
    }
  }

  get previousTripId() {
    if (this.tripIndex !== -1 && this.tripIndex === this.trips.length - 1) {
      return null;
    }

    if (this.tripIndex === -1) {
      if (this.singleTrip && this.singleTrip.previous) {
        return this.singleTrip.previous.id.toString()
      }
    } else {
      const nextTripId = this.trips[this.tripIndex + 1].id
      return nextTripId.toString();
    }
  }

  get nextTripId() {
    if (this.tripIndex === 0) {
      return null;
    }

    if (this.tripIndex === -1) {
      if (this.singleTrip && this.singleTrip.next) {
        return this.singleTrip.next.id.toString();
      }
    } else {
      const previousTripId = this.trips[this.tripIndex - 1].id
      return previousTripId.toString();
    }
  }

  get saveErrorExists(): TripDetailsError[] {
    return getTripDetailError(this.tripMutation);
  }

  private addTypeSwitch(type: AddType) {
    this.addType = type;
    this.$emit('addType', type, this.mutation);
  }

  private handleInput(copy: TripMutation) {
    this.mutation = copy;
  }

  private switchEditMode(edit: boolean) {
    this.$emit('switchEditMode', edit);
  }

  private deleteTripButton() {
    if (window.confirm(this.deleteText)) {
      this.deleteTripHandling();
    }
  }

  private handleCancelButton() {
    if (!this.unsavedChanges || window.confirm(this.exitText)) {
      if (this.addMode) {
        goBackWithFallback(this.$router, !!this.from.name, this.backDeleteLocation);
      } else {
        this.switchEditMode(false);
        this.mutation = tripToTripMutation(this.singleTrip);
      }
    }
  }

  private async handleSaveButton() {
    if (this.addMode) {
      this.loadingNewTrip = true;
      try {
        const createdTrip = await this.createTrip({ trip: this.mutation });

        if (this.createTripStatus === LoadState.Success && this.addAnotherTrip && !this.addAnotherDisabled) {
          this.showErrorMessage = false;
          setTimeout(() => this.showErrorMessage = true, 500);

          this.$emit('addAnotherTrip', this.addType, createdTrip);
        } else if (this.createTripStatus === LoadState.Success) {
          goBackWithFallback(this.$router, !!this.from.name, { name: 'myTrips', query: { vehicle: this.mutation.vehicleId.toString() } });
        }
      } finally {
        this.loadingNewTrip = false;
      }
    } else if (this.editMode && this.saveErrorExists.length < 1) {
      if (this.unsavedChanges) {
        this.loadingNewTrip = true;
        try {
          const newTrip = await this.updateTrip({ tripId: this.tripId, trip: this.mutation });
          this.singleTrip = {...this.singleTrip, ...newTrip};
          this.switchEditMode(false);
        } finally {
          this.loadingNewTrip = false;
        }
      } else {
        this.switchEditMode(false);
      }
    } else if (!this.editMode && this.mutation) {
      // see changes immediately
      this.singleTrip.pilot = this.pilots.find(p => p.id === this.mutation.pilotId);
      this.singleTrip.tag = this.tags.find(t => t.id === this.mutation.tagId);

      const newTrip = await this.updateTrip({ tripId: this.tripId, trip: this.mutation, validateConflict: false });
      this.singleTrip = { ...this.singleTrip, ...newTrip };
    }
  }

  private errorExists(tripId, otherTripId) {
    if (this.tripErrorsStatus !== LoadState.Success) {
      return false;
    }

    return this.tripErrors.some(te => te.type !== 'gapOdometer' && ((te.first.id === tripId && te.second.id === otherTripId) || (te.first.id === otherTripId && te.second.id === tripId)))
  }

  public goToPreviousTrip() {
    if (this.previousTripId) {
      if (!this.unsavedChanges || window.confirm(this.exitText)) {
        this.loadingNewTrip = true;
        this.goToTrip(this.previousTripId);
      }
    }
  }

  public goToNextTrip() {
    if (this.nextTripId) {
      if (!this.unsavedChanges || window.confirm(this.exitText)) {
        this.loadingNewTrip = true;
        this.goToTrip(this.nextTripId);
      }
    }
  }

  public goToTrip(id: string) {
    this.$router.replace({name: 'myTripsEdit', params: {id: id}});
  }
}
